import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, firstValueFrom } from "rxjs";
import { jwtDecode } from "jwt-decode";
import { environment } from "../../environments/environment";
import { Router } from '@angular/router';

@Injectable({
  providedIn: "root",
})
export class AuthService {
  // private baseAuthApiUrl = 'http://localhost:3002/api/auth';
  private baseAuthApiUrl = `${environment.authApiUrl}/auth`;
  private baseUserApiUrl =
    `${environment.userManagementApiUrl}/user`;

  private loginUrl = `${this.baseAuthApiUrl}/tokens`;
  private refreshTokenUrl = `${this.baseAuthApiUrl}/tokens/refresh`;

  private readonly openTabsKey = 'openTabs';
  private readonly tokenKey = 'accessToken';
  private readonly tabId = Date.now().toString();

  constructor(private http: HttpClient, private router: Router) {
  }

  /*
   *
   * Login method to call the login endpoint
   * @param email User's email
   * @param password User's password
   * @returns Observable with the server response
   */
  login(email: string, password: string): Observable<any> {
    const payload = { email, password };
    return this.http.post(this.loginUrl, payload, { withCredentials: true });
  }

  resetPassword(
    userId: string,
    oldPassword: string,
    newPassword: string
  ): Observable<any> {
    const payload = { email: userId, password: oldPassword, newPassword };
    return this.http.post(this.loginUrl, payload, { withCredentials: true });
  }

  respondMfa(
    sessionKey: string,
    email: string,
    mfaCode: number,
    challengeName: string
  ) {
    const mfaUrl = `${this.loginUrl}/respondMfa`;
    const payload = { session: sessionKey, email, mfaCode, challengeName };
    return this.http.post(mfaUrl, payload, { withCredentials: true });
  }

  initiateTotpSetup(accessToken: string): Observable<{ secretCode: string }> {
    const initiateTotpUrl = `${this.loginUrl}/initiatetotp`;
    const payload = { accessToken };
    return this.http.post<{ secretCode: string }>(initiateTotpUrl, payload, {
      withCredentials: true,
    });
  }

  verifyTotpSetup(accessToken: string, mfaCode: string) {
    const verifyTotpUrl = `${this.loginUrl}/verifytotp`;
    const payload = { accessToken, totpCode: mfaCode };
    return this.http.post(verifyTotpUrl, payload, { withCredentials: true });
  }

  enableMfa() {
    const enableMfaUrl = `${this.baseUserApiUrl}/enable-mfa`;
    return this.http.post(enableMfaUrl, { withCredentials: true });
  }

  private isTokenValid(token: string): boolean {
    try {
      const decoded: any = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp > currentTime; // Token is valid if expiry time is in the future
    } catch (error) {
      return false; // Token is invalid
    }
  }

  areTokensValid(): boolean {
    const accessToken = localStorage.getItem("accessToken");
    const tokenId = localStorage.getItem("idToken");
    const isAccessTokenValid = accessToken && this.isTokenValid(accessToken);
    const isIdTokenValid = tokenId && this.isTokenValid(tokenId);

    return (isAccessTokenValid && isIdTokenValid) || false;
  }

  async refreshTokens(): Promise<boolean> {
    const idToken = localStorage.getItem("idToken");
    if (!idToken) {
      console.warn("No ID token found");
      return false;
    }

    try {
      const response: any = await firstValueFrom(
        this.http.post(this.refreshTokenUrl, { idToken: idToken }, { withCredentials: true })
      );

      if (response) {
        this.handleAuthTokens(response);
        console.log("Tokens refreshed.");
        return true;
      } else {
        console.error("Token refresh failed: Invalid response");
        return false;
      }
    } catch (error) {
      console.error("Token refresh failed", error);
      return false;
    }
  }

  isAdmin(token?: string): boolean {
    const idToken = token || localStorage.getItem("idToken");


    if (idToken && this.isTokenValid(idToken)) {
      try {
        const decoded: any = jwtDecode(idToken);
        return (
          decoded["custom:user_role"] === "HospitalAdmin" ||
          decoded["custom:user_role"] === "GraftAdmin"
        );
      } catch (error) {
        console.error("Error decoding token:", error);
        return false;
      }
    }

    return false;
  }

  isStaff(token?: string): boolean {
    const idToken = token || localStorage.getItem("idToken");

    if (idToken && this.isTokenValid(idToken)) {
      try {
        const decoded: any = jwtDecode(idToken);
        return (
          decoded["custom:user_role"] === "Provider" ||
          decoded["custom:user_role"] === "Coordinator"
        );
      } catch (error) {
        console.error("Error decoding token:", error);
        return false;
      }
    }

    return false;
  }

  /**
   * Handle successful login
   * @param response The login response object
   */
  handleAuthTokens(response: any): void {
    // Store the accessToken and idToken in localStorage
    if (response.accessToken && response.idToken) {
      localStorage.setItem("accessToken", response.accessToken);
      localStorage.setItem("idToken", response.idToken);
    } else {
      console.error("Missing tokens in the response");
    }
  }

  getUserId(): string {
    const idToken = localStorage.getItem("idToken");

    if (idToken) {
      try {
        // Decode the JWT to extract the user ID
        const payload = JSON.parse(atob(idToken.split(".")[1]));
        return payload.email; // Replace with the correct field for user ID in your token
      } catch (error) {
        throw new Error("Invalid token format");
      }
    }

    throw new Error("User ID not found in local storage");
  }

  getAccessToken(): string | null {
    return localStorage.getItem("accessToken");
  }

  getIdToken(): string | null {
    return localStorage.getItem("idToken");
  }

  /**
   * Logout method to clear tokens
   */
  logout(): void {
    localStorage.clear();
    // You may also call a logout endpoint to invalidate tokens server-side
  }

  signUpAndTriggerEmailVerification(email: string, password: string): Observable<any> {
    const signUpUrl = `${this.baseAuthApiUrl}/signup/code`;
    const payload = {
      email, password
    }
    return this.http.post(signUpUrl, payload);
  }

  verifySignupCode(email: string, code: string): Observable<any> {
    const verifyCodeUrl = `${this.baseAuthApiUrl}/signup/verify`;
    const payload = {
      email, code
    }
    return this.http.post(verifyCodeUrl, payload);
  }

  resendSignupCode(email: string) {
    return this.http.post(`${this.baseAuthApiUrl}/signup/resend-verify`, { email });
  }

  forgotPassword(email: string): Observable<any> {
    const url = `${this.baseAuthApiUrl}/forgot-password`;
    return this.http.post(url, { email });
  }

  confirmForgotPassword(email: string, code: string, newPassword: string): Observable<any> {
    const url = `${this.baseAuthApiUrl}/confirm-forgot-password`;
    return this.http.post(url, {
      email,
      code,
      newPassword,
    });
  }

  // signup(email: string, password: string, firstName: string, lastName: string): Observable<any>{
  //   return of(null);
  // }
  getCurrentUser(): { name: string, email: string, image?: string, role: string } | null {
    const idToken = this.getIdToken();

    if (idToken && this.isTokenValid(idToken)) {
      try {
        const decoded: any = jwtDecode(idToken);
        return {
          name: decoded.name || decoded.given_name || '',
          email: decoded.email || '',
          image: decoded.picture || null,
          role: decoded["custom:user_role"] || ''
        };
      } catch (error) {
        console.error("Failed to decode token:", error);
        return null;
      }
    }

    return null;
  }

}
